import { DisplayTheme, LayoutMode } from '../../../../types';
import { Spacing, Theme } from '../../../css/baseStyles';

type BaseStyleProps = {
  theme: Theme;
  marginTop: number;
  isHighContrast: boolean;
  deviceHeight: number;
  layoutMode: LayoutMode;
  isChunked?: boolean;
};
export const getBaseStyles = ({ theme, marginTop, deviceHeight, isHighContrast, layoutMode, isChunked = false }: BaseStyleProps) => {
  // Adjust the centre opacity of the edge-glow radial mask depending on the current display theme.
  // A lower alpha on dark themes prevents the glow from dimming text too aggressively while
  // preserving the original visual on light/sepia themes.
  const edgeGlowCenterAlpha = theme.type === DisplayTheme.Dark ? 0.4 : 0.6;
  return `
  <style id="RWMainStyle">
    * {
      box-sizing: border-box;
    }

    .hidden-image {
      display: none;
    }

    Body {
      margin: 0;
      width: 100%;
      position: relative;
      margin: auto;
    }

    .rw-header-container {
      min-height: 150px;
    }

    body.not-on-first-chunk .rw-header-container {
      display: none;
    }

    .document-root {
      transition: ${isHighContrast ? '' : 'opacity 0.2s ease'};
      opacity: 1;
      min-height: 100vh;
    }

    #edge-glow-mask-container {
      ${layoutMode === LayoutMode.VerticalPagination || !isChunked ? '' : `
      /* Edge-glow overflow mask (static layer) */
      transition: mask-size 0.2s ease-out;
      mask-image: linear-gradient(#000 0 0), radial-gradient(ellipse, rgba(0,0,0,${edgeGlowCenterAlpha}) 0 50%, rgba(0,0,0,0) 90%);
      mask-composite: exclude;
      mask-repeat: no-repeat, no-repeat;
      mask-origin: content-box;
      /* Start with zero-sized radial mask so glow is hidden until JS enlarges it */
      mask-size: 100% 100%, 0px 0px;
      mask-position: 0 0, center bottom;
      `}
      position: relative;
    }

    .transparent {
      opacity: 0!important;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
    }

    .bottom-margin {
      height: ${Spacing.readerBottomPadding}px;
    }

    .overflow-hidden-enforcer {
      position: relative;
      overflow-x: clip;
    }

    .enhancing-transcript-message-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-left: 16px;
      padding-right: 16px;
    }

    .failed-highlights-message-container {
      padding-left: 16px;
      padding-right: 16px;
    }

    .truncated-paywall-article-message-container {
      padding-left: 8px;
      padding-right: 8px;
      margin-left: 4px;
      margin-right: 4px;
      border-radius: 8px;
      margin-bottom: 8px;
      margin-top: 8px;
      color: ${theme.secondaryText};
      border: 1px solid ${theme.borderSecondary};
      font-family: var(--ui-font);
    }

    .truncated-paywall-article-message-container a {
      color: ${theme.textInteractive};
    }

    .truncated-paywall-article-message-container.hidden {
      display: none;
    }

    .failed-highlights-message,
    .enhancing-transcript-message {
      color: ${theme.secondaryText};
      margin-bottom: 16px;
      font-family: var(--ui-font);
    }

    .failed-highlights-message button {
      display: block;
      margin-top: 0.75em;
      margin-left: 0;
      font-size: 14px;
    }

    img {
      max-width: 100%;
      max-height: ${deviceHeight * 0.5}px !important;
    }

    .image-before-load {
      height: 200px;
    }

    .document-root {
    }

    .document-container {
      width: 100%;
      position: relative;
      background-color: ${theme.readerBackground};
    }

    .rtl {
      direction: rtl;
      text-align: right;
    }

    .document-content {
      margin: auto;
      box-sizing: border-box;
      overflow: hidden;
      max-width: ${layoutMode === LayoutMode.HorizontalPagination ? '100%' : `${Spacing.maxDocumentWidth}px`};
      padding-left: 0px;
      padding-right: 0px;
    }

    .document-content img {
      object-fit: contain;
    }

    .movable {
      transform: translateY(0px)!important;
    }

    .image-margin {
      top: 0;
      height: ${marginTop}px;
      z-index: -1;
      width: 100%;
      overflow: hidden;
    }

    .metadata svg {
      fill: ${theme.secondaryText};
      margin-right: 4px;
    }

    .search-highlighter {
      position: absolute;
      font-size: 15px;
      display: flex;
      justify-content: center;
      align-items: end;
      background: ${theme.findInDocumentHighlight};
      outline: 2px solid var(--focus-ring-color);
      border-radius: 4px;
      width: 1px;
      padding: 0 1px;
      left: -1px;
    }
    .search-highlighter-pop {
      animation: push 0.3s linear 1;
    }

    ::highlight(search-results-active) {
      background: ${theme.findInDocumentHighlightActive};
    }

    ::highlight(search-results) {
      background: ${theme.findInDocumentHighlight};
    }

    @keyframes push{
      50%  {transform: scale(1.2);}
    }

    #bottom-divider {
      height: 30px;
      background: ${theme.readerBackground};
    }

    .end-of-reading-container {
      margin: 0 auto;
      margin-bottom: 150px;
      display: block;
      padding-top: 38px;
      width: calc(100% - ${Spacing.baseHorizontalMargin} * 2);
      max-width: 375px;
      background-color: ${theme.readerBackground};
      padding-bottom: 24px;
      padding-left: ${Spacing.baseHorizontalMargin}px;
      padding-right: ${Spacing.baseHorizontalMargin}px;
      overflow: hidden;
      clear: both;
    }

    #end-of-reading-button {
      fill: ${theme.buttonPrimaryDefaultColor}
    }

    #end-of-reading-text {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
      font-size: 17px;
      fill: ${theme.textInteractive};
    }

    /* End of reading keyframes */
    @keyframes a0_t { 0% { transform: translate(187.5px,36px) scale(0,0); animation-timing-function: cubic-bezier(0.372625,0,0.867035,0.778726); } 48.35% { transform: translate(187.5px,36px) scale(0.525,0.535555); animation-timing-function: cubic-bezier(0.645231,0.631791,1,1); } 50% { transform: translate(187.5px,36px) scale(0.603701,0.617328); animation-timing-function: cubic-bezier(0.804425,0.026502,0.58,1); } 53.35% { transform: translate(187.5px,36px) scale(1.003701,1.003113); } 58.35% { transform: translate(187.5px,36px) scale(1,1); } 100% { transform: translate(187.5px,36px) scale(1,1); } }
    @keyframes btn_t { 0% { transform: translate(-32px,-32px); } 50% { transform: translate(-32px,-32px); animation-timing-function: cubic-bezier(0.595,0,0.685,1.505); } 58.35% { transform: translate(-168px,-32px); } 100% { transform: translate(-168px,-32px); } }
    @keyframes btn_o { 0% { opacity: 0; } 39.15% { opacity: 0; } 50% { opacity: 1; } 58.35% { opacity: 1; } 100% { opacity: 1; } }
    @keyframes btn_w { 0% { width: 64px; } 50% { width: 64px; animation-timing-function: cubic-bezier(0.595,0,0.685,1.505); } 58.35% { width: 336px; } 100% { width: 336px; } }
    @keyframes text_t { 0% { transform: translate(187.5px,42.181877px) scale(0.8,0.8); } 51.65% { transform: translate(187.5px,42.181877px) scale(0.8,0.8); animation-timing-function: cubic-bezier(0.595,0,0.685,1.505); } 58.35% { transform: translate(187.5px,42.181877px) scale(1,1); } 100% { transform: translate(187.5px,42.181877px) scale(1,1); } }
    @keyframes text_o { 0% { opacity: 0; } 50% { opacity: 0; } 58.35% { opacity: 1; } 100% { opacity: 1; } }
    @keyframes checkmark_t { 0% { transform: translate(187.5px,36px) scale(2.6,2.6) translate(-16.0004px,-16.6004px); } 14.15% { transform: translate(187.5px,36px) scale(0.317,0.317) translate(-16.0004px,-16.6004px); } 50% { transform: translate(187.5px,36px) scale(1.6,1.6) translate(-16.0004px,-16.6004px); } 100% { transform: translate(187.5px,36px) scale(1.6,1.6) translate(-16.0004px,-16.6004px); } }
    @keyframes checkmark_o { 0% { opacity: 0; } 14.15% { opacity: 0; animation-timing-function: cubic-bezier(0.585,0,0.983145,0.469); } 50% { opacity: 1; animation-timing-function: steps(1); } 50.85% { opacity: 0; } 100% { opacity: 0; } }
    @keyframes check2_t { 0% { transform: translate(183.510605px,36px) rotate(0deg); } 45% { transform: translate(183.510605px,36px) rotate(0deg); } 48.35% { transform: translate(183.510604px,36.000002px) rotate(0deg); } 50% { transform: translate(187.511px,38.888527px) rotate(-44deg); } 100% { transform: translate(187.511px,38.888527px) rotate(-44deg); } }
    @keyframes check2_o { 0% { opacity: 1; } 50% { opacity: 1; } 50.85% { opacity: 0; } 100% { opacity: 0; } }
    @keyframes check2_sw { 0% { stroke-width: 0px; } 48.35% { stroke-width: 2px; } 100% { stroke-width: 2px; } }
    @keyframes check1_t { 0% { transform: translate(183.510605px,36px) rotate(0deg) translateX(0.010605px); } 45% { transform: translate(183.510605px,36px) rotate(0deg) translateX(0.010605px); } 48.35% { transform: translate(183.510604px,36.000002px) rotate(0deg) translateX(0.010605px); } 50% { transform: translate(183.334058px,39.282374px) rotate(43deg) translateX(0.010605px); } 100% { transform: translate(183.334058px,39.282374px) rotate(43deg) translateX(0.010605px); } }
    @keyframes check1_o { 0% { opacity: 1; } 50% { opacity: 1; } 50.85% { opacity: 0; } 100% { opacity: 0; } }
    @keyframes check1_sw { 0% { stroke-width: 0px; } 48.35% { stroke-width: 2px; } 100% { stroke-width: 2px; } }
    @keyframes line_sw { 0% { stroke-width: 0px; } 48.35% { stroke-width: 2px; } 100% { stroke-width: 2px; } }

    .youtube-current-teleprompter {
      background: ${theme.teleprompt};
      color: white;
    }

    .chunk-loading-spinner {
      position: fixed;
      width: 100%;
      height: 100%;
      z-index: 10000;
      right: 0px;
      top: 0px;
      display: none;
      user-select: none;
      -webkit-user-select: none;
      opacity: 0;
      filter: ${theme.chunkLoadingSpinnerFilter};
    }.chunk-loading-spinner svg {
      display: block;
      margin: auto;
    }
    .chunk-loading-spinner-active {
      display: flex;
      opacity: 1;
    }

    .absolutely-positioned-content {
      display: none;
    }

    .rw-chunk-container {
      position: relative;
      min-height: 1px;
      transition: opacity 0.4s ease, mask-size 0.2s ease-out;
    }

    .centeredElementDebug {
      outline: 2px solid red;
    }

    .centeredElementDebug-visible {
      outline: 2px solid green;
    }

    .centered-element-debug-indicator {
      position: fixed;
      top: 150px;
      right: 10px;
      background: rgba(255, 0, 0, 0.9);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-family: monospace;
      font-size: 12px;
      font-weight: bold;
      z-index: 10000;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
      max-width: 200px;
      word-break: break-all;
    }

    .centered-element-debug-indicator.visible {
      background: rgba(0, 128, 0, 0.9);
    }

    .centered-element-debug-indicator.not-visible {
      background: rgba(255, 0, 0, 0.9);
    }

    .disable-scroll {
      overflow: hidden!important;
    }

    .page-divider {
      position: absolute;
      display: none;
      background: ${theme.readerBackground};
      user-select: none;
      -webkit-user-select: none;
      z-index: 2;
    }
    .next-chunk-arrow {
      filter: ${theme.nextChunkArrowFilter};
      height: 18px;
      width: 18px;
      transform: rotateZ(-90deg) translateY(-3px) translateX(-3px);
    }
    .next-chunk-arrow-container {
      position: absolute;
      bottom: 82px;
      width: 100%;
      z-index: 1000;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: ${theme.secondaryText}
    }
    .next-chunk-arrow-container span {
      font-family: sans-serif;
      font-size: 13px;
    }

    .previous-chunk-arrow {
      filter: ${theme.nextChunkArrowFilter};
      height: 18px;
      width: 18px;
      transform: rotateZ(90deg) translateY(-3px) translateX(3px);
    }
    .previous-chunk-arrow-container {
      position: absolute;
      top: 82px;
      width: 100%;
      z-index: 1000;
      pointer-events: none;
      user-select: none;
      -webkit-user-select: none;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: ${theme.secondaryText};
      opacity: 0; /* hidden by default */
    }
    .previous-chunk-arrow-container span {
      font-family: sans-serif;
      font-size: 13px;
    }

    .next-chunk-arrow-container,
    .previous-chunk-arrow-container {
      transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s ease-out;
    }
  </style>
  `;
};
